/**
 * MODULE: _shared/frontend-shell/src/components/assistant/assistantContextFollowUpChips.ts
 * PURPOSE: Merge brain context suggested actions with route fallbacks for post-reply follow-up chips.
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-07-08 [2.2.1487] — Brain context advisor_execute chips label as operator "Do it".
 * RECENT_PRIOR: 2026-06-06 [2.2.1314] — coaching_snapshot prompts from POST /assistant/context/resolve.
 */

import type { AssistantContextResponse, AssistantContextAction } from '@shared/lib/frontend/api/assistantContextApi';
import type { StreamSuggestedAction } from '../../hooks/assistant/streamSuggestedActions';
import {
  followUpChipFromStreamAction,
  type AssistantFollowUpChipItem,
} from '../../hooks/assistant/assistantOperatorMode';
import {
  isNavigateAssistantAction,
  resolveAssistantContextAction,
  assistantActionPromptText,
} from '@shared/lib/frontend/utils/assistantContextActionHandlers';
import {
  getAssistantFollowUpChips,
  type AssistantRouteContext,
} from '../../design-system/components/AiAssistant/assistantContextualPrompts';
import { collectProactiveAdvisorPrompts } from './assistantProactiveAdvisorUtils';
import { collectEmailTemplateWelcomeChips } from './assistantWelcomePrompts';

export type { AssistantFollowUpChipItem } from '../../hooks/assistant/assistantOperatorMode';

function contextActionToFollowUpChip(
  action: AssistantContextAction,
  index = 0,
): AssistantFollowUpChipItem | null {
  const resolved = resolveAssistantContextAction(action);
  if (
    resolved.kind === 'navigate' ||
    resolved.kind === 'open_email_template_create' ||
    resolved.kind === 'open_sales_target_set'
  ) {
    return null;
  }
  const prompt = assistantActionPromptText(resolved);
  if (!prompt) return null;
  const modeRaw = (action.execution_mode || '').toLowerCase();
  const executionMode = modeRaw === 'advisor_execute' ? 'advisor_execute' : 'prompt';
  if (
    executionMode === 'advisor_execute' ||
    action.source === 'action_first_primary' ||
    (typeof action.action === 'string' && action.action.startsWith('action_first_'))
  ) {
    return followUpChipFromStreamAction(
      {
        label: (action.label || prompt).trim(),
        prompt_text: prompt,
        execution_mode: executionMode,
        action: action.action,
        source: action.source,
      },
      index,
    );
  }
  return {
    label: (action.label || prompt).trim(),
    prompt,
    executionMode,
  };
}

/** Structured follow-ups with operator primary chip support. */
export function collectContextFollowUpChipItems(
  context: AssistantContextResponse | null,
  pageContext?: AssistantRouteContext | null,
  limit = 3,
  streamSuggestedActions?: StreamSuggestedAction[] | null,
): AssistantFollowUpChipItem[] {
  const seen = new Set<string>();
  const out: AssistantFollowUpChipItem[] = [];

  const push = (chip: AssistantFollowUpChipItem) => {
    const key = chip.prompt.toLowerCase();
    if (!chip.prompt || seen.has(key)) return;
    seen.add(key);
    out.push(chip);
  };

  if (streamSuggestedActions?.length) {
    streamSuggestedActions.slice(0, limit).forEach((action, index) => {
      push(followUpChipFromStreamAction(action, index));
    });
    if (out.length >= limit) return out.slice(0, limit);
  }

  for (const [index, action] of (context?.suggested_next_actions ?? []).entries()) {
    if (isNavigateAssistantAction(action)) continue;
    const chip = contextActionToFollowUpChip(action, index);
    if (!chip) continue;
    push(chip);
    if (out.length >= limit) return out.slice(0, limit);
  }

  for (const chip of collectEmailTemplateWelcomeChips(context)) {
    if (chip.kind === 'open_create_modal') continue;
    push({ label: chip.label, prompt: chip.prompt, executionMode: 'prompt' });
    if (out.length >= limit) return out.slice(0, limit);
  }

  for (const prompt of collectProactiveAdvisorPrompts(context)) {
    push({ label: prompt, prompt, executionMode: 'prompt' });
    if (out.length >= limit) return out.slice(0, limit);
  }

  for (const chip of getAssistantFollowUpChips(pageContext)) {
    push({ label: chip, prompt: chip, executionMode: 'prompt' });
    if (out.length >= limit) return out.slice(0, limit);
  }

  return out.slice(0, limit);
}

/** Context-first follow-ups; falls back to route-aware defaults when brain context is empty. */
export function collectContextFollowUpPrompts(
  context: AssistantContextResponse | null,
  pageContext?: AssistantRouteContext | null,
  limit = 3,
  streamSuggestedActions?: StreamSuggestedAction[] | null,
): string[] {
  return collectContextFollowUpChipItems(
    context,
    pageContext,
    limit,
    streamSuggestedActions,
  ).map((chip) => chip.prompt);
}
